import { EventType } from "@/analytics/analytics-events";
import {
  getBcfIntegrationTypeForIElementType,
  getExternalAnnotationProviderName,
} from "@/components/ui/annotations/external-annotation-utils";
import {
  applyAsyncMutations,
  useUpdateStoreAfterMutations,
  useUpdateStoreFromMutations,
} from "@/components/ui/annotations/mutations-optimistic-update";
import { DeleteElementDialogContent } from "@/components/ui/delete-element-dialog-content";
import { useErrorHandlers } from "@/errors/components/error-handling-context";
import { addBackgroundTask } from "@/store/background-tasks/background-tasks-slice";
import {
  addIElementsToStore,
  removeIElementFromStore,
} from "@/store/ielements-store-utilities";
import {
  useAppDispatch,
  useAppSelector,
  useAppStore,
} from "@/store/store-hooks";
import { MutationTask, MutationTaskType } from "@/utils/background-tasks";
import { useDialog } from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { assert, generateGUID } from "@faro-lotv/foundation";
import {
  ExternalMarkupIElement,
  IElementMarkup,
  isIElementExternalMarkup,
  isIElementGenericAnnotation,
  isIElementSection,
} from "@faro-lotv/ielement-types";
import { selectAncestor } from "@faro-lotv/project-source";
import {
  BackgroundTaskState,
  createMutationDeleteElement,
  useApiClientContext,
} from "@faro-lotv/service-wires";
import { DateTime } from "luxon";
import { useCallback, useState } from "react";

export type UseDeleteAnnotationReturn = {
  /** Function to call when the annotation needs to be removed */
  removeAnnotation(): Promise<void>;

  /** True while the mutation is in progress */
  isRemovingAnnotation: boolean;
};

/**
 * Return a function to remove an annotation and a flag to know when the project is mutating
 *
 * @param markup the markup whose annotation must be removed
 * @returns the function to call to remove the annotation and a flag to check for project mutations
 */
export function useDeleteAnnotation(
  markup: IElementMarkup | ExternalMarkupIElement,
): UseDeleteAnnotationReturn {
  const { getState } = useAppStore();
  const { projectApiClient } = useApiClientContext();
  const { createDialog } = useDialog();
  const [isRemovingAnnotation, setIsRemoving] = useState(false);
  const { handleErrorWithToast } = useErrorHandlers();
  const dispatch = useAppDispatch();
  const externalAnnotationProviderName = isIElementExternalMarkup(markup)
    ? getExternalAnnotationProviderName(
        getBcfIntegrationTypeForIElementType(markup.type),
      )
    : undefined;
  const externalAnnotationWarningMessage = `The corresponding annotation saved on ${externalAnnotationProviderName} will not be deleted.`;

  // At app startup, 'annotationToDelete' may also be undefined because not all
  // markups' parents are loaded.
  const annotationToDelete = useAppSelector(
    selectAncestor(markup, isIElementGenericAnnotation),
  );

  const updateStoreFromMutations = useUpdateStoreFromMutations();
  const updateStoreAfterMutations = useUpdateStoreAfterMutations();

  const removeAnnotation = useCallback(async () => {
    const shouldRemove = await createDialog({
      title: "Delete Element?",
      confirmText: "Delete",
      content: (
        <DeleteElementDialogContent
          name="the annotation"
          warningMessage={
            externalAnnotationProviderName
              ? externalAnnotationWarningMessage
              : undefined
          }
          dark
        />
      ),
      variant: "danger",
      dark: true,
    });

    if (!shouldRemove) {
      Analytics.track(EventType.cancelAnnotationDeletion);
      return;
    }

    Analytics.track(EventType.confirmAnnotationDeletion);

    // Preemptive hide the annotation
    setIsRemoving(true);
    try {
      let annotationToDeleteId = annotationToDelete?.id;
      if (!annotationToDeleteId) {
        // If the markup is not yet loaded, fetch it
        const elements = await projectApiClient.getAllIElements({
          descendantIds: [markup.id],
        });
        annotationToDeleteId = elements.find((e) =>
          isIElementGenericAnnotation(e),
        )?.id;
        addIElementsToStore(elements, dispatch);
      }

      assert(
        annotationToDeleteId,
        "The annotation cannot be removed because its parent was not loaded",
      );

      const targetIElement = selectAncestor(
        markup,
        isIElementSection,
      )(getState());
      assert(targetIElement);

      const isoDate = DateTime.now().toISO();
      const task: MutationTask = {
        type: "Mutation",
        id: generateGUID(),
        iElementId: markup.id,
        rootId: targetIElement.id,
        createdAt: isoDate,
        changedAt: isoDate,
        devMessage: "Deleting annotation",
        state: BackgroundTaskState.created,
        progress: 1,
        shouldPreventWindowClose: true,
        mutationType: MutationTaskType.DeleteAnnotation,
      };
      dispatch(addBackgroundTask(task));

      const mutations = [createMutationDeleteElement(annotationToDeleteId)];

      updateStoreFromMutations(mutations);

      try {
        await applyAsyncMutations(projectApiClient, mutations);
        // This step is needed even if we are updating the store from the backend, because
        // the annotation-list checks for any markup iElement and the updateProject
        // callback only adds new data, but does not delete the existing one
        removeIElementFromStore(annotationToDeleteId, dispatch);
      } catch (error) {
        handleErrorWithToast({
          title: "Removal Failed",
          error,
        });
      }

      updateStoreAfterMutations([
        { taskId: task.id, targetIElementId: targetIElement.id },
      ]);
    } catch (error) {
      handleErrorWithToast({
        title: "Removal Failed",
        error,
      });
      // Show again if removal failed
      setIsRemoving(false);
    }
  }, [
    createDialog,
    externalAnnotationProviderName,
    externalAnnotationWarningMessage,
    annotationToDelete?.id,
    markup,
    getState,
    dispatch,
    updateStoreFromMutations,
    projectApiClient,
    handleErrorWithToast,
    updateStoreAfterMutations,
  ]);

  return {
    isRemovingAnnotation,
    removeAnnotation,
  };
}
